/* eslint-disable react/require-default-props */
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';

import Button from '../Button/Button';
import PlayerControlSettings from '../PlayerControlSettings/PlayerControlSettings';

import './PlayerBar.scss';

export interface PlayerBarProps {
  playbackRate?: number;
  unlocked?: boolean;
  autoAdvance?: boolean;
  playing?: boolean;
  playedSeconds?: number;
  duration?: number;
  showCaptions?: boolean;
  updatePlaybackRate: (value: number) => void;
  updateAutoAdvance: (value: boolean) => void;
  updateShowCaptions: () => void;
  onPlaying: (value: boolean) => void;
  onSeekTo: (value: number, isReplay?: boolean) => void;
  onSeeking: (value: boolean) => void;
  setSettingsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  showPlayerAndResetTimer?: () => void;
  autoHideable?: boolean;
  isBeingShown?: boolean;
}

const secondsTohhmmss = (totalSeconds: number) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds - hours * 3600) / 60);
  let seconds = totalSeconds - hours * 3600 - minutes * 60;
  let result = '';

  // round seconds
  seconds = Math.round(seconds * 100) / 100;

  if (hours > 0) {
    result += (hours < 10 ? '0' + hours : hours) + ':';
  }
  result += minutes < 10 ? '0' + minutes : minutes;
  result += ':' + (seconds < 10 ? '0' + seconds : seconds);
  return result;
};

const PlayerBar = React.forwardRef<HTMLDivElement, PlayerBarProps>(
  (
    {
      playbackRate = 1,
      unlocked = false,
      autoAdvance = false,
      playing = false,
      playedSeconds = 0,
      duration = 0,
      showCaptions = false,
      updatePlaybackRate,
      updateAutoAdvance,
      updateShowCaptions,
      onPlaying,
      onSeekTo,
      onSeeking,
      setSettingsOpen,
      showPlayerAndResetTimer,
      autoHideable,
      isBeingShown,
      ...props
    },
    ref,
  ) => {
    const playerSettingsRef = useRef<HTMLDivElement>(null);
    const [settingsVisible, setSettingsVisible] = useState(false);
    const changeSettingsVisible = ({ hide }: { hide?: boolean }) => {
      setSettingsVisible(hide ? false : !settingsVisible);
      // These optional passed functions will likely
      // not be present on the admin/preview area.
      if (setSettingsOpen) {
        setSettingsOpen(hide ? false : !settingsVisible);
      }
      if (showPlayerAndResetTimer) {
        showPlayerAndResetTimer();
      }
    };
    const onPlayerBarClick = (e) => {
      // Hide the settings menu if there is a click that is:
      // not on the settings popup, not on a child of the settings popup,
      // and not on the PlayerBar settings icon (won't allow it to pop up
      // the first place).
      if (
        playerSettingsRef.current &&
        playerSettingsRef.current !== e?.target &&
        !playerSettingsRef.current.contains(e.target) &&
        // One of these gets triggered for mouse interaction and the other
        // for keyboard interaction.
        !e?.target.classList.contains('PlayerBar-settings-button') &&
        !e?.target.classList.contains('PlayerBar-settings-icon')
      ) {
        changeSettingsVisible({ hide: true });
      }
    };
    useEffect(() => {
      window.addEventListener('click', onPlayerBarClick);
      return () => {
        window.removeEventListener('click', onPlayerBarClick);
      };
    }, [settingsVisible]);

    const [showPlayTooltip, setShowPlayTooltip] = useState(false);
    const playerBar = useRef<HTMLInputElement>(null);

    const playPauseButton = (
      <Button
        className={classNames('player-btn', {
          pause: playing,
          play: !playing,
        })}
        onClick={(e) => {
          if (playing) {
            onPlaying(false);
            setShowPlayTooltip(true);
          } else {
            onPlaying(true);
            setShowPlayTooltip(false);
          }
        }}
        tooltipId="player-playpause-btn"
        tooltip="Click play to resume."
        tooltipPlacement="top"
        showTooltip={
          !playing &&
          showPlayTooltip &&
          // The tooltip shouldn't display if the PlayerBar can be
          // auto-hid and isn't fully displayed.
          ((autoHideable && isBeingShown) || !autoHideable)
        }
      >
        <i className="material-icons">{playing ? 'pause' : 'play_arrow'}</i>
      </Button>
    );

    if (playerBar.current) {
      const percent = ((playedSeconds - 0) / (duration - 0)) * 100;
      playerBar.current.style.setProperty('--webkitProgressPercent', `${percent}%`);
    }

    return (
      <div {...props} ref={ref} className="PlayerBar">
        {/* LEFT CONTENT */}
        <div className="left-content">
          {/* Left Buttons */}
          <div className="left-buttons">
            {playPauseButton}
            <Button
              className="player-btn replay"
              onClick={() => {
                const toJump = Math.max(playedSeconds - 10, 0);
                onSeekTo(toJump, true);
              }}
              title="Jump Back 10 Seconds"
            >
              <i className="material-icons">replay_10</i>
            </Button>
          </div>

          {/* Current Time / Total Duration */}
          <div className="player-time">
            {`${secondsTohhmmss(
              duration - playedSeconds < 0.01 ? Math.round(duration) : Math.floor(playedSeconds),
            )} / ${secondsTohhmmss(Math.round(duration))}`}
          </div>
        </div>

        {/* TIMELINE */}
        <div className="timeline">
          <input
            className="player-bar"
            ref={playerBar}
            type="range"
            min={0}
            max={duration}
            step="any"
            value={playedSeconds}
            onChange={(e) => {
              const seekValue = Math.floor(parseFloat(e.target.value));
              if (unlocked || seekValue < playedSeconds) {
                onSeekTo(seekValue);
              }
            }}
            onMouseDown={() => {
              onSeeking(true);
            }}
            onMouseUp={() => {
              onSeeking(false);
            }}
            disabled={!unlocked}
          />
        </div>

        {/* RIGHT CONTENT */}
        <div className="right-content">
          {/* Right Buttons */}
          <div className="right-buttons">
            <Button
              className="player-btn settings PlayerBar-settings-button"
              onClick={() => {
                changeSettingsVisible({});
              }}
              active={settingsVisible}
            >
              {/* Animate Gear 90deg when engaged */}
              <CSSTransition
                in={settingsVisible}
                timeout={300}
                classNames="settings-gear-animation"
              >
                <i className="material-icons PlayerBar-settings-icon">settings</i>
              </CSSTransition>
            </Button>
          </div>
        </div>
        {settingsVisible && (
          <PlayerControlSettings
            playbackRate={playbackRate}
            unlocked={unlocked}
            autoAdvance={autoAdvance}
            showCaptions={showCaptions}
            updatePlaybackRate={updatePlaybackRate}
            updateAutoAdvance={updateAutoAdvance}
            updateShowCaptions={updateShowCaptions}
            ref={playerSettingsRef}
          />
        )}
      </div>
    );
  },
);

export default PlayerBar;
